import {application} from 'configs/app';
import EventEmitter from 'eventemitter3';
import api, {ApiError} from 'services/Api';
import store from 'store';
import SnackbarUtil from 'utils/snackbar';
import {handleMessage} from 'utils/websocket-handler';

const _singleton = Symbol();
const _singletonEnforcer = Symbol();

export const WEBSOCKET_STATUS = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  CLOSED: 'CLOSED',
  DUPLICATED: 'DUPLICATED',
};

export default class WebSocketApi extends EventEmitter {
  _websocket = null;
  _pending = new Map();
  _uid = 0;
  _strictConnection = true;

  constructor(enforcer) {
    super();
    if (enforcer !== _singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }

    // هماهنگ‌سازی با logout اصلی
    window.addEventListener('logout', () => {
      this._strictConnection = false;
      this.disconnect(true);
    });
  }

  static get instance() {
    if (!this[_singleton]) {
      this[_singleton] = new WebSocketApi(_singletonEnforcer);
    }
    return this[_singleton];
  }

  // ----------------------------
  //          LIFECYCLE
  // ----------------------------

  _onOpen = async () => {
    try {
      this._heartbeatInteral = setInterval(() => {
        this.invoke({_: 'HeartbeatRequest'}, {api: {withAuth: false}});
      }, 10000);

      this._websocket._connectionPromise.resolve();

      await this.authenticate();
      this._websocket._authPromise.resolve();

      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CONNECTED);
    } catch (e) {
      this.disconnect();
      throw e;
    }
  };

  _onClose = (event) => {
    if (event.code === 4001) {
      this._strictConnection = false; // duplicated login
    }
    this._onTerminate();

    if (event.code === 4001) {
      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.DUPLICATED);
      // یک دیوایس دیگر با همین اکانت وارد شده؛ اگر تماسی باز است باید بسته شود
      window.dispatchEvent(new Event('session-duplicated'));
    }
  };

  _onTerminate = () => {
    store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CLOSED);

    if (this._heartbeatInteral) {
      clearInterval(this._heartbeatInteral);
      this._heartbeatInteral = undefined;
    }

    if (this._websocket) {
      const err = new WebSocketApiError(
        undefined,
        new Error(),
        'ارتباط با سرور قطع شد',
      );

      this._websocket._connectionPromise.reject(err);
      this._websocket._authPromise.reject(err);

      this._pending.forEach((p) => p.reject(err));
      this._pending.clear();

      this._websocket = null;
    }

    // ⛔ بعد از logout دیگر reconnect نکن
    if (this._strictConnection && localStorage.getItem('refreshToken')) {
      setTimeout(() => {
        this.ensureConnection();
      }, 800);
    }
  };

  // ----------------------------
  //        MESSAGE
  // ----------------------------

  _onMessage = async (message) => {
    const response = JSON.parse(message.data);

    // اگر WS هم 401 بده → دقیقاً مثل API باید logout کنیم
    if (response.header.status === 401) {
      api.logout(); // این event logout را هم trigger می‌کند
      return;
    }

    let request = null;

    if (response.id) {
      request = this._pending.get(response.id);

      if (response.header.status >= 400) {
        const msg = response.body?.message || 'خطایی رخ داده است';

        request.reject(new WebSocketApiError(response.body, new Error(), msg));
      } else {
        request.resolve(response);
      }
    }

    handleMessage(response, request);

    if (response?.body?._ === 'PushSocketResponse') {
      this.emit('PushSocket', response.body);
    }
  };

  // ----------------------------
  //       CONNECTION MGMT
  // ----------------------------

  ensureConnection = (withAuth = true) => {
    if (!this._websocket) {
      store.getActions().websocket.updateStatus(WEBSOCKET_STATUS.CONNECTING);

      this._websocket = new WebSocket(application.webSocketEndpoint);
      this._websocket.onmessage = this._onMessage;
      this._websocket.onopen = this._onOpen;
      this._websocket.onclose = this._onClose;
      this._websocket.onerror = this._onTerminate;

      this._websocket._connectionPromise = {};
      this._websocket._connectionPromise.promise = new Promise(
        (resolve, reject) => {
          this._websocket._connectionPromise.resolve = resolve;
          this._websocket._connectionPromise.reject = reject;
        },
      );

      this._websocket._authPromise = {};
      this._websocket._authPromise.promise = new Promise((resolve, reject) => {
        this._websocket._authPromise.resolve = resolve;
        this._websocket._authPromise.reject = reject;
      });
    }

    return withAuth
      ? this._websocket._authPromise.promise
      : this._websocket._connectionPromise.promise;
  };

  disconnect = (force = false) => {
    this._strictConnection = !force;

    if (this._websocket) {
      this._websocket.close();
    }
  };

  // ----------------------------
  //           AUTH
  // ----------------------------

  authenticate = async () => {
    const tokenObj = await api.getCachedToken();

    if (!tokenObj?.access_token) {
      this.disconnect(true);
      return;
    }

    return this.invoke(
      {
        _: 'AuthenticateRequest',
        authorization: 'Bearer ' + tokenObj.access_token,
      },
      {api: {withAuth: false}},
    );
  };

  // ----------------------------
  //          INVOKE
  // ----------------------------

  invoke = async (body, options) => {
    const apiOptions = {
      emitNotification: false,
      withAuth: true,
      ...((options && options.api) || {}),
    };

    if (options) delete options.api;

    const id = ++this._uid;

    try {
      await this.ensureConnection(apiOptions.withAuth);

      this._websocket.send(
        JSON.stringify({
          id,
          body,
        }),
      );

      return new Promise((resolve, reject) => {
        this._pending.set(id, {
          body,
          resolve: (value) => {
            this._pending.delete(id);
            resolve(value);
          },
          reject: (e) => {
            this._pending.delete(id);

            let err =
              e instanceof ApiError
                ? e
                : new WebSocketApiError(undefined, e, 'خطایی رخ داده است');

            reject(err);

            if (apiOptions.emitNotification) {
              SnackbarUtil.error(err.message);
            }
          },
        });
      });
    } catch (e) {
      if (!(e instanceof ApiError)) {
        throw new WebSocketApiError(undefined, e, 'خطایی رخ داده است');
      }
      throw e;
    }
  };
}

class WebSocketApiError extends ApiError {
  constructor(...params) {
    super(...params);

    this.name = 'WebSocketApiError';

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, WebSocketApiError);
    }
  }
}
